/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <quantity-selector-component> that allows users to select and update product quantities in bulk quick order forms. 
It handles increasing, decreasing, and setting quantities, updates product row prices and totals, manages UI feedback (like showing/hiding the remove bin icon), and instantly updates the Shopify cart via AJAX if enabled. 
The component also syncs quantities with the cart, enforces min/max rules, and updates item counts and subtotals. 
It is registered as a custom element if not already defined.

*/

import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, QuantitySelectorBulkUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    
    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange(event);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

   /**
   * Syncs the quantity input value with the current cart quantity for this variant.
   * Updates the input value to match the cart, or sets to 0 if not present.
   * Optionally, you can call this after a cart update or on component init.
   */
  syncQuantityWithCart() {
    const input = this.refs.quantityInput;
    const variantId = input.dataset.variantId;
    if (!variantId) return;
    
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const item = cart.items.find(i => String(i.variant_id) === String(variantId));
        input.value = item ? item.quantity : 0;
      })
      .catch(error => {
        console.error('Failed to sync quantity with cart:', error);
      });
  }

  //Update the product row with the new quantity and price
  updateProductRow() {
      const row = this.refs.quantityInput.closest('.product-row');
      if (!row) return;
      const quantity = this.refs.quantityInput.value;
      const variantId = this.refs.quantityInput.dataset.variantId;
      const priceElements = row.querySelectorAll('.variant-price');
      const totalElements = row.querySelectorAll('.variant-total');

      if (priceElements && totalElements) {
        const price = parseFloat(priceElements[0].dataset.price);
        const total = (price * quantity).toFixed(2);

        // Update the price and total elements
        priceElements.forEach(el => el.textContent = (price * quantity).toFixed(2));
        totalElements.forEach(el => el.textContent = total);
      }
  }

  updateProductSubtotal() {
    const bulk = this.closest('.quick-add-bulk');
    if (!bulk) return;
    const productSubtotals = bulk.querySelectorAll('.product-subtotal');
    // If no product subtotal element, exit early
    if (!productSubtotals) return;

    // Calculate the total from all variant totals
    const total = Array.from(bulk.querySelectorAll('.variant-total'))
      .reduce((sum, el) => sum + parseFloat(el.textContent) || 0, 0);
    productSubtotals.forEach(el => el.textContent = total.toFixed(2));
  }

  //Add/Remove bin icon
  showRemoveBin() {
      const container = this.refs.quantityInput.closest('.quantity-selector-container');
      if (!container) return;

      const removeBin = container.querySelector('.quantity__remove-bin');
      if (!removeBin) return;

     if (this.refs.quantityInput.value > 0) {
        removeBin.classList.remove('hidden');
    } else {
        removeBin.classList.add('hidden');
    }
  }

  updateCountItems(event) {
    let itemCountElements = null;
    if (event && event.target) {
      itemCountElements = event.target.closest('.quick-add-bulk')?.querySelectorAll('.items-count');
    }
    if (!itemCountElements) {
      itemCountElements = this.querySelectorAll('.items-count');
    }
    if (!itemCountElements) return;

    // If no event (e.g. after removeAll), set to 0
    if (!event) {
      itemCountElements.forEach(el => el.textContent = 0);
    } else {
      itemCountElements.forEach(el => el.textContent = this.getTotalItemCount());
    }
  }

  getTotalItemCount() {
      const bulk = this.closest('.quick-add-bulk');
    if (!bulk) return 0;

    // Select all containers for both desktop and mobile
    const containers = bulk.querySelectorAll(
      '.quantity-selector-container[data-variant-id], .quantity-selector-container-mobile[data-variant-id]'
    );

    return Array.from(containers).reduce((total, container) => {
      // Only count if container is visible (not display: none)
      if (container.offsetParent === null) return total;
      const quantityInput = container.querySelector('[ref="quantityInput"]');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 0;
      return total + quantity;
    }, 0);
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    

    const { quantityInput } = this.refs;
    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    if (typeof quantityInput.dataset.cartLine !== 'undefined') {
      quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
    } else {
      
      // Inside #onQuantityChange in component-quantity-selector.js
      const variantId = quantityInput.dataset.variantId;
      const newValue = parseInt(quantityInput.value);

      const event = new CustomEvent('quickadd:quantitychange', {
        bubbles: true,
        detail: {
          variantId,
          quantity: newValue
        }
      });
      this.dispatchEvent(event);
      
      // quantityInput.dispatchEvent(new QuantitySelectorBulkUpdateEvent(newValue, Number(quantityInput.dataset.variantId)));
    }

    this.updateProductRow();
    this.showRemoveBin();
    this.updateCountItems(event);
    this.updateProductSubtotal();

    // Instant add to cart
    if (this.hasAttribute('data-instant-add')) {
      this.#instantAddToCart(newValue);
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (parseInt(newValue) < parseInt(min)) quantityInput.value = min;
    if (parseInt(newValue) > parseInt(max)) quantityInput.value = max;
  };

  /**
   * Updates the Shopify cart instantly if data-instant-add is set.
   * @param {number} quantity
   */

 
  #instantAddToCart(quantity) {
    const input = this.refs.quantityInput;
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    this.#disableCartItems();

    const body = JSON.stringify({
      updates: {
        [variantId]: Number(quantity)
      }
    });

    fetch(Theme.routes.cart_update_url,  fetchConfig('json', { body }))
      .then(res => res.json())
      .then(cart => {
        this.dispatchEvent(
          new CartUpdateEvent(
            {},
            this.dataset.sourceId || null,
            {
              source: 'quantity-selector',
              variantId: variantId,
              itemCount: cart.item_count,
            }
          )
        );
      })
      .catch(error => {
        this.dispatchEvent(
          new CartErrorEvent(
            this.dataset.sourceId || null,
            error.message || 'Cart update failed'
          )
        );
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
